import { Fragment, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import {
  emailLocalPart,
  FEED_EMAIL_RE,
  type FeedPrivacyScope,
  isFeedPrivacyVocabulary,
  looksLikeEmail,
} from "@/features/feed/feedPrivacy";
import { formatPersonName } from "@/lib/formatPersonName";
import { normalizeContractRef } from "@/lib/formatContract";
import { formatUsd } from "@/types/booking";

export type { FeedPrivacyScope };

/** Money and contract-reference tokens embedded in prose. */
const INLINE_SENSITIVE_RE =
  /(\$[\d,]+(?:\.\d+)?(?:[kKmM])?|\b[A-Z]{2,4}-\d{4}-\d{3}\b|\b(?:[A-Z]{2,4}\s+)?GEN-\d{4}\b|\bcontract\s+[A-Z0-9-]+\b)/gi;

interface SensitiveProps {
  children: ReactNode;
  className?: string;
}

/** Blurs children when privacy masking is enabled. */
export function Sensitive({ children, className }: SensitiveProps) {
  const { preferences } = useUserPreferences();

  if (!preferences.hideSensitiveData) {
    return <span className={className}>{children}</span>;
  }

  return (
    <span
      className={cn(
        "sensitive-blur inline-block max-w-full align-middle",
        className,
      )}
      aria-hidden
    >
      {children}
    </span>
  );
}

export function usePrivacyMasking(): boolean {
  return useUserPreferences().preferences.hideSensitiveData;
}

interface SensitiveTextProps {
  value?: string | number | null;
  children?: ReactNode;
  className?: string;
}

/** Party names, contact labels, and other PII text. */
export function SensitiveText({
  value,
  children,
  className,
}: SensitiveTextProps) {
  const content = children ?? value ?? "—";
  return <Sensitive className={className}>{content}</Sensitive>;
}

interface SensitiveIdentityProps {
  value?: string | null;
  className?: string;
}

/**
 * Compact chrome identity (top-bar user / org). Clips the blur so glass pills
 * stay crisp instead of smearing into neighbouring controls.
 */
export function SensitiveIdentity({
  value,
  className,
}: SensitiveIdentityProps) {
  const { preferences } = useUserPreferences();
  const text = value?.trim() || "—";

  if (!preferences.hideSensitiveData) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span
      className={cn(
        "inline-flex max-w-full overflow-hidden rounded-[3px] align-middle",
        className,
      )}
      aria-hidden
    >
      <span className="sensitive-blur-identity max-w-full truncate">{text}</span>
    </span>
  );
}

interface SensitiveContractRefProps {
  value?: string | null;
  /** When the carrier logo is shown beside this ref, strip its SCAC prefix. */
  carrier?: string | null;
  className?: string;
}

/** Agent contract token (carrier prefix stripped when the logo is adjacent). */
export function SensitiveContractRef({
  value,
  carrier,
  className,
}: SensitiveContractRefProps) {
  const text = normalizeContractRef(value, carrier) || "—";
  if (text === "—") {
    return <span className={className}>{text}</span>;
  }
  return <SensitiveText className={className} value={text} />;
}

interface SensitiveMoneyProps {
  amount?: number | null;
  /** Pre-formatted money string (e.g. "$48.2k" widgets). */
  formatted?: string | null;
  className?: string;
}

/** Currency amounts — formats USD when `amount` is provided. */
export function SensitiveMoney({
  amount,
  formatted,
  className,
}: SensitiveMoneyProps) {
  const { preferences } = useUserPreferences();
  const text = formatted ?? formatUsd(amount ?? undefined);

  if (!preferences.hideSensitiveData) {
    return <span className={cn("tabular-nums", className)}>{text}</span>;
  }

  return (
    <span
      className={cn("sensitive-blur-money tabular-nums", className)}
      aria-hidden
    >
      {text}
    </span>
  );
}

/** True when a dashboard counter string looks like currency. */
export function isMoneyDisplayValue(value: string): boolean {
  return /^\s*\$/.test(value);
}

/** Labels for savings, pricing, and other monetary meta rows. */
export function isMoneyLabel(label: string): boolean {
  return /saving|price|frt|all-in|revenue|cost|amount/i.test(label);
}

export function isContractLabel(label: string): boolean {
  return /^contract$/i.test(label.trim());
}

export function shouldMaskAsMoney(label: string, value: string): boolean {
  return isMoneyDisplayValue(value) || isMoneyLabel(label);
}

export function shouldMaskSensitiveMeta(label: string, value: string): boolean {
  return shouldMaskAsMoney(label, value) || isContractLabel(label);
}

function isInlineSensitiveToken(part: string): boolean {
  if (!part) return false;
  if (/^\$/.test(part)) return true;
  if (/^[A-Z]{2,4}-\d{4}-\d{3}$/i.test(part)) return true;
  if (/^(?:[A-Z]{2,4}\s+)?GEN-\d{4}$/i.test(part)) return true;
  if (/^contract\s+[A-Z0-9-]+$/i.test(part)) return true;
  return false;
}

function renderSensitiveTokens(text: string, keyPrefix = ""): ReactNode {
  const parts = text.split(INLINE_SENSITIVE_RE);

  return parts.map((part, i) => {
    if (!isInlineSensitiveToken(part)) {
      return <Fragment key={`${keyPrefix}${i}`}>{part}</Fragment>;
    }

    if (/^\$/.test(part)) {
      return (
        <span
          key={`${keyPrefix}${i}`}
          className="sensitive-blur-money tabular-nums"
          aria-hidden
        >
          {part}
        </span>
      );
    }

    return (
      <Sensitive key={`${keyPrefix}${i}`}>{part}</Sensitive>
    );
  });
}

interface SensitiveMetaValueProps {
  label: string;
  value: string;
  className?: string;
}

/** Dashboard / card meta row value — masks savings, currency, and contract refs. */
export function SensitiveMetaValue({
  label,
  value,
  className,
}: SensitiveMetaValueProps) {
  const hide = usePrivacyMasking();

  if (!hide || !shouldMaskSensitiveMeta(label, value)) {
    return <span className={className}>{value}</span>;
  }

  if (isContractLabel(label)) {
    return <SensitiveContractRef value={value} className={className} />;
  }

  return <SensitiveMoney formatted={value} className={className} />;
}

interface SensitiveInlineTextProps {
  text: string;
  className?: string;
}

/** Prose with embedded currency and contract-ref tokens. */
export function SensitiveInlineText({
  text,
  className,
}: SensitiveInlineTextProps) {
  const hide = usePrivacyMasking();

  if (!hide) {
    return <span className={className}>{text}</span>;
  }

  return <span className={className}>{renderSensitiveTokens(text)}</span>;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Party names, emails, and booking ids that may appear inside feed titles / summaries. */
export function collectFeedPrivacyTokens(
  scope?: FeedPrivacyScope,
): string[] {
  const raw = [
    scope?.consignee,
    scope?.sales_person,
    scope?.opened_by,
    scope?.reference_id ?? undefined,
    ...(scope?.extra ?? []),
  ].filter((value): value is string => !!value?.trim());

  const tokens = new Set<string>();
  for (const token of raw) {
    const trimmed = token.trim();
    if (isFeedPrivacyVocabulary(trimmed)) continue;
    tokens.add(trimmed);
    if (looksLikeEmail(trimmed)) {
      const local = emailLocalPart(trimmed);
      if (local) {
        tokens.add(local);
        const display = formatPersonName(local);
        if (display && display !== "—" && display !== local) tokens.add(display);
      }
      continue;
    }
    const display = formatPersonName(trimmed);
    if (display && display !== "—" && display !== trimmed) tokens.add(display);
  }

  return [...tokens].sort((a, b) => b.length - a.length);
}

function isMaskedFeedPart(part: string, tokens: string[]): boolean {
  if (looksLikeEmail(part)) return true;
  return tokens.some((token) => token.toLowerCase() === part.toLowerCase());
}

function renderFeedSensitiveText(
  text: string,
  scope?: FeedPrivacyScope,
): ReactNode {
  const tokens = collectFeedPrivacyTokens(scope);
  const tokenAlt = tokens
    .map((token) =>
      looksLikeEmail(token)
        ? escapeRegExp(token)
        : `\\b${escapeRegExp(token)}\\b`,
    )
    .join("|");
  const emailAlt = FEED_EMAIL_RE.source;
  const pattern = new RegExp(
    `(${[tokenAlt, emailAlt].filter(Boolean).join("|")})`,
    "gi",
  );
  const parts = text.split(pattern);

  return parts.map((part, i) => {
    if (!part) return null;
    if (isMaskedFeedPart(part, tokens)) {
      return <Sensitive key={i}>{part}</Sensitive>;
    }
    return <Fragment key={i}>{renderSensitiveTokens(part, `${i}-`)}</Fragment>;
  });
}

interface SensitiveFeedTextProps {
  text: string;
  scope?: FeedPrivacyScope;
  className?: string;
}

/** Feed titles/summaries — masks embedded party names plus currency / contract refs. */
export function SensitiveFeedText({
  text,
  scope,
  className,
}: SensitiveFeedTextProps) {
  const hide = usePrivacyMasking();

  if (!hide) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>{renderFeedSensitiveText(text, scope)}</span>
  );
}

/** Split inline markdown bold and mask sensitive tokens inside each segment. */
interface SensitiveDocumentPreviewProps {
  children: ReactNode;
  className?: string;
}

/** Blurs inline PDF/image previews when privacy masking is enabled. */
export function SensitiveDocumentPreview({
  children,
  className,
}: SensitiveDocumentPreviewProps) {
  const hide = usePrivacyMasking();

  if (!hide) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={cn("relative", className)}>
      <div className="sensitive-blur h-full w-full [&_iframe]:pointer-events-none [&_img]:pointer-events-none">
        {children}
      </div>
      <div
        className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm"
        aria-hidden
      >
        <p className="max-w-xs rounded-md border border-border/60 bg-card/95 px-3 py-2 text-center text-xs text-muted-foreground">
          Document preview hidden while privacy masking is on
        </p>
      </div>
    </div>
  );
}

/** Split inline markdown bold and mask sensitive tokens inside each segment. */
export function renderSensitiveInlineMarkdown(text: string, hide: boolean): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const inner = part.slice(2, -2);
      return (
        <strong key={i} className="font-semibold text-foreground">
          {hide ? renderSensitiveInlineMarkdown(inner, hide) : inner}
        </strong>
      );
    }

    return hide ? (
      <Fragment key={i}>{renderSensitiveTokens(part, `${i}-`)}</Fragment>
    ) : (
      part
    );
  });
}
